import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Alert } from 'react-bootstrap';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import TradingDashboard from '../components/TradingDashboard';
import IndicesTable from '../components/IndicesTable';
import AddIndexModal from '../components/AddIndexModal';
import EditIndexModal from '../components/EditIndexModal';
import DeleteIndexModal from '../components/DeleteIndexModal';
import StoredLevelsTable from '../components/StoredLevelsTable';
import ManageLevelsModal from '../components/ManageLevelsModal';
import TradingContextProvider from '../components/TradingContextProvider';
import UITest from '../components/UITest';

const Welcome = () => {
  const [configs, setConfigs] = useState({});
  const [strikeData, setStrikeData] = useState({});
  const [selectedLevels, setSelectedLevels] = useState({});
  const [tradingEnabled, setTradingEnabled] = useState({});
  const [optionData, setOptionData] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentISTTime, setCurrentISTTime] = useState(new Date());
  const [haltTime, setHaltTime] = useState(() => {
    const defaultHalt = new Date();
    defaultHalt.setHours(15, 15, 0, 0);
    return defaultHalt;
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(null);
  const [showDeleteLevelsModal, setShowDeleteLevelsModal] = useState(null);
  const [showDeleteIndexModal, setShowDeleteIndexModal] = useState(null);
  const [levelsToDelete, setLevelsToDelete] = useState({ ce: [], pe: [] });
  const [tradingState, setTradingState] = useState();
  const [loading, setLoading] = useState(false);

  const expectedHeaders = [
    'token',
    'symbol',
    'name',
    'expiry',
    'strike',
    'lotsize',
    'instrumenttype',
    'exch_seg',
    'tick_size',
  ];

  const excelDateToJSDate = (serial) => {
    const utcDays = Math.floor(serial - 25569);
    const utcValue = utcDays * 86400;
    return new Date(utcValue * 1000);
  };

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const systemOffset = now.getTimezoneOffset() * 60 * 1000;
      const istOffset = 5.5 * 60 * 60 * 1000;
      const istTime = new Date(now.getTime() + (systemOffset !== -istOffset ? istOffset : 0));
      setCurrentISTTime(istTime);
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchHaltTime = async () => {
      const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/halt-time`);
      const storedHaltTime = response.data.haltTime;
      if (storedHaltTime) {
        const [hours, minutes] = storedHaltTime.split(':').map(Number);
        const newHalt = new Date();
        newHalt.setHours(hours, minutes, 0, 0);
        setHaltTime(newHalt);
      }
    };
    fetchHaltTime();

    const getTradingState = async () => {
      const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/trading-state`);
      setTradingState(response.data.isTradingActive);
    };
    getTradingState();

    const fetchIndices = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/indices`);
        const newConfigs = response.data.reduce((acc, { name, config }) => ({ ...acc, [name]: config }), {});
        const newStrikeData = response.data.reduce((acc, { name, strikeData }) => ({ ...acc, [name]: strikeData }), {});
        const newLevels = response.data.reduce((acc, { name, selectedLevels }) => ({
          ...acc,
          [name]: selectedLevels || { ceLevels: [], peLevels: [] },
        }), {});
        setConfigs(newConfigs);
        setStrikeData(newStrikeData);
        setSelectedLevels(newLevels);
        setTradingEnabled(response.data.reduce((acc, { name, config }) => ({ ...acc, [name]: config.tradingEnabled }), {}));
        setOptionData(
          response.data.reduce(
            (acc, { name }) => ({
              ...acc,
              [name]: { latestCe: null, latestPe: null },
            }),
            {}
          )
        );
        setError('');
      } catch (error) {
        setError(`Error fetching indices: ${error.response?.data || error.message}`);
      }
    };
    fetchIndices();
  }, []);

  const handleSelectedLevels = useCallback(async (ceLevels, peLevels, index = 'NIFTY', workingFigure) => {
    if (!tradingEnabled[index]) {
      setError(`Trading disabled for ${index}, levels ignored`);
      return;
    }
    try {
      await axios.put(`${process.env.REACT_APP_SERVER_URL}/indices/${index}/levels`, { selectedLevels: { ceLevels, peLevels } });
      setSelectedLevels((prev) => ({
        ...prev,
        [index]: { ceLevels, peLevels },
      }));
      if (workingFigure !== undefined) {
        await axios.put(`${process.env.REACT_APP_SERVER_URL}/indices/${index}`, {
          config: { ...configs[index], workingFigure },
        });
        setConfigs((prev) => ({ ...prev, [index]: { ...prev[index], workingFigure } }));
      }
      setSuccess(`${index} levels updated successfully!`);
      setError('');
    } catch (error) {
      setError(`Error updating ${index} levels: ${error.response?.data || error.message}`);
    }
  }, [tradingEnabled, configs]);

  const handleDeleteLevels = useCallback((index, levels) => {
    if (!index || !selectedLevels[index]) {
      setError('Invalid index or no levels found');
      setShowDeleteLevelsModal(null);
      setLevelsToDelete({ ce: [], pe: [] });
      return;
    }
    setSelectedLevels((prev) => {
      const updatedLevels = { ...prev };
      updatedLevels[index] = {
        ceLevels: updatedLevels[index].ceLevels.filter((l) => !levels.ce.includes(l.level)),
        peLevels: updatedLevels[index].peLevels.filter((l) => !levels.pe.includes(l.level)),
      };
      return updatedLevels;
    });
    axios.put(`${process.env.REACT_APP_SERVER_URL}/indices/${index}/levels`, {
      selectedLevels: {
        ceLevels: selectedLevels[index].ceLevels.filter((l) => !levels.ce.includes(l.level)),
        peLevels: selectedLevels[index].peLevels.filter((l) => !levels.pe.includes(l.level)),
      },
    }).then(() => {
      setSuccess(`Deleted selected levels for ${index}`);
      setError('');
    }).catch((error) => {
      setError(`Error deleting levels for ${index}: ${error.response?.data || error.message}`);
    });
    setShowDeleteLevelsModal(null);
    setLevelsToDelete({ ce: [], pe: [] });
  }, [selectedLevels]);

  const handleLevelCheckboxChange = useCallback((type, level, checked) => {
    setLevelsToDelete((prev) => {
      const updatedLevels = { ...prev };
      if (checked) {
        updatedLevels[type] = [...updatedLevels[type], level];
      } else {
        updatedLevels[type] = updatedLevels[type].filter((l) => l !== level);
      }
      return updatedLevels;
    });
  }, []);

  const handleToggleTrading = (indexName) => {
    setTradingEnabled((prev) => {
      const updated = { ...prev, [indexName]: !prev[indexName] };
      setConfigs((prevConfigs) => ({
        ...prevConfigs,
        [indexName]: { ...prevConfigs[indexName], tradingEnabled: updated[indexName] }
      }));
      axios.put(`${process.env.REACT_APP_SERVER_URL}/indices/${indexName}`, {
        config: { ...configs[indexName], tradingEnabled: updated[indexName] },
      }).catch((error) => {
        setError(`Error updating trading status for ${indexName}: ${error.response?.data || error.message}`);
      });
      return updated;
    });
    setSuccess(`Trading for ${indexName} turned ${!tradingEnabled[indexName] ? 'On' : 'Off'}`);
    setError('');
  };

  const handleAddIndex = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const indexName = e.target.indexName.value.toUpperCase();
    const config = {
      indexExchange: e.target.indexExchange.value,
      optionsExchange: e.target.optionsExchange.value,
      profitPercentage: parseFloat(e.target.profitPercentage.value) || 0,
      lotSize: parseFloat(e.target.lotSize.value) || 0,
      maxLots: parseFloat(e.target.maxLots.value) || 0,
      symboltoken: e.target.symboltoken.value || '',
      step: parseFloat(e.target.step.value) || 50,
      tradingEnabled: false,
      workingFigure: parseFloat(e.target.workingFigure.value),
    };

    if (config.workingFigure === undefined || isNaN(config.workingFigure)) {
      setError('Working Figure is required');
      setLoading(false);
      return;
    }

    const ceFile = e.target.ceFile.files[0];
    const peFile = e.target.peFile.files[0];
    if (!ceFile || !peFile) {
      setError('CE and PE Excel files are required');
      setLoading(false);
      return;
    }

    try {
      const processExcelFile = (file, optionType) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (evt) => {
            try {
              const data = evt.target.result;
              const workbook = XLSX.read(data, { type: 'array' });
              const firstSheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[firstSheetName];
              const rawJson = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

              if (rawJson.length === 0) {
                reject(new Error(`The ${optionType} sheet is empty.`));
                return;
              }

              const fileHeaders = Object.keys(rawJson[0]).map((header) => header.toLowerCase().trim());
              const headersValid =
                fileHeaders.length === expectedHeaders.length &&
                expectedHeaders.every((expHeader, index) => expHeader === fileHeaders[index]);

              if (!headersValid) {
                reject(new Error(`Invalid header structure in ${optionType} file.`));
                return;
              }

              const processedJson = rawJson.map((row) => {
                let expiryDate =
                  typeof row.expiry === 'number'
                    ? excelDateToJSDate(row.expiry)
                    : new Date(row.expiry);
                const formattedExpiry = isNaN(expiryDate.getTime())
                  ? row.expiry
                  : format(expiryDate, 'dd-MMM-yy').toUpperCase();

                return {
                  token: Number(row.token),
                  symbol: row.symbol,
                  name: row.name,
                  expiry: formattedExpiry,
                  strike: Number(row.strike),
                  lotsize: Number(row.lotsize),
                  instrumenttype: row.instrumenttype,
                  exch_seg: row.exch_seg,
                  tick_size: Number(row.tick_size),
                };
              });

              const filteredJson = processedJson.filter((row) =>
                optionType === 'CE' ? row.symbol.includes('CE') : row.symbol.includes('PE')
              );

              if (filteredJson.length === 0) {
                reject(new Error(`No ${optionType} options found in ${optionType} file.`));
                return;
              }

              resolve(filteredJson);
            } catch (err) {
              reject(new Error(`Error processing ${optionType} file: ${err.message}`));
            }
          };
          reader.onerror = () => reject(new Error(`Error reading ${optionType} file.`));
          reader.readAsArrayBuffer(file);
        });
      };

      const [ceStrikes, peStrikes] = await Promise.all([
        processExcelFile(ceFile, 'CE'),
        processExcelFile(peFile, 'PE'),
      ]);

      await axios.post(`${process.env.REACT_APP_SERVER_URL}/indices`, {
        name: indexName,
        config,
        strikeData: { ce: ceStrikes, pe: peStrikes },
      });

      setConfigs((prev) => ({ ...prev, [indexName]: config }));
      setStrikeData((prev) => ({ ...prev, [indexName]: { ce: ceStrikes, pe: peStrikes } }));
      setOptionData((prev) => ({ ...prev, [indexName]: { latestCe: null, latestPe: null } }));
      setSelectedLevels((prev) => ({ ...prev, [indexName]: { ceLevels: [], peLevels: [] } }));
      setTradingEnabled((prev) => ({ ...prev, [indexName]: false }));
      setSuccess(`${indexName} added successfully!`);
      setError('');
      setShowAddForm(false);
    } catch (error) {
      setError(error.message || `Failed to add index: ${error.response?.data || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditIndex = async (e, indexName) => {
    e.preventDefault();
    const updatedConfig = {
      indexExchange: e.target.indexExchange.value,
      optionsExchange: e.target.optionsExchange.value,
      profitPercentage: parseFloat(e.target.profitPercentage.value) || 0,
      lotSize: parseFloat(e.target.lotSize.value) || 0,
      maxLots: parseFloat(e.target.maxLots.value) || 0,
      symboltoken: e.target.symboltoken.value || '',
      step: parseFloat(e.target.step.value) || 50,
      tradingEnabled: tradingEnabled[indexName],
      workingFigure: parseFloat(e.target.workingFigure.value),
    };
    if (updatedConfig.workingFigure === undefined || isNaN(updatedConfig.workingFigure)) {
      setError('Working Figure is required');
      return;
    }
    try {
      await axios.put(`${process.env.REACT_APP_SERVER_URL}/indices/${indexName}`, {
        config: updatedConfig,
      });
      setConfigs((prev) => ({ ...prev, [indexName]: updatedConfig }));
      setSuccess(`${indexName} updated successfully!`);
      setError('');
      setShowEditForm(null);
    } catch (error) {
      setError(`Error updating ${indexName}: ${error.response?.data || error.message}`);
    }
  };

  const handleDeleteIndex = async (indexName) => {
    if (indexName === 'NIFTY') {
      setError('Cannot delete NIFTY (default index)');
      setShowDeleteIndexModal(null);
      return;
    }
    try {
      await axios.delete(`${process.env.REACT_APP_SERVER_URL}/indices/${indexName}`);
      setConfigs((prev) => { const newConfigs = { ...prev }; delete newConfigs[indexName]; return newConfigs; });
      setStrikeData((prev) => { const newStrikeData = { ...prev }; delete newStrikeData[indexName]; return newStrikeData; });
      setOptionData((prev) => { const newOptionData = { ...prev }; delete newOptionData[indexName]; return newOptionData; });
      setSelectedLevels((prev) => { const newLevels = { ...prev }; delete newLevels[indexName]; return newLevels; });
      setTradingEnabled((prev) => { const newTradingEnabled = { ...prev }; delete newTradingEnabled[indexName]; return newTradingEnabled; });
      setSuccess(`${indexName} deleted successfully!`);
      setError('');
      setShowDeleteIndexModal(null);
    } catch (error) {
      setError(`Error deleting ${indexName}: ${error.response?.data || error.message}`);
      setShowDeleteIndexModal(null);
    }
  };

  const styles = useMemo(() => ({
    dataBox: { marginTop: '20px', padding: '15px', backgroundColor: '#ffffff', borderRadius: '4px' },
    statusIndicator: { display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', marginRight: '8px' },
  }), []);

  return (
    <TradingContextProvider selectedLevels={selectedLevels} setSelectedLevels={setSelectedLevels}>
      <Container className="bg-white px-4 pb-5">
        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success.split('\n').map((line, i) => <div key={i}>{line}</div>)}
        </Alert>}
        <Row>
          <Col md={12}>
            <TradingDashboard
              tradingState={tradingState}
              setTradingState={setTradingState}
              currentISTTime={currentISTTime}
              haltTime={haltTime}
              setHaltTime={setHaltTime}
              setSuccess={setSuccess}
              setError={setError}
              styles={styles}
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col>
            <IndicesTable
              configs={configs}
              tradingEnabled={tradingEnabled}
              handleToggleTrading={handleToggleTrading}
              setShowAddForm={setShowAddForm}
              setShowEditForm={setShowEditForm}
              setShowDeleteIndexModal={setShowDeleteIndexModal}
            />
          </Col>
        </Row>
        <AddIndexModal
          show={showAddForm}
          onHide={() => setShowAddForm(false)}
          handleAddIndex={handleAddIndex}
          loading={loading}
        />
        <EditIndexModal
          show={showEditForm !== null}
          onHide={() => setShowEditForm(null)}
          indexName={showEditForm}
          configs={configs}
          handleEditIndex={handleEditIndex}
        />
        <DeleteIndexModal
          show={showDeleteIndexModal !== null}
          onHide={() => setShowDeleteIndexModal(null)}
          indexName={showDeleteIndexModal}
          handleDeleteIndex={handleDeleteIndex}
        />
        <Row className="mt-3">
          <Col>
            <StoredLevelsTable
              selectedLevels={selectedLevels}
              setShowDeleteLevelsModal={setShowDeleteLevelsModal}
              styles={styles}
            />
          </Col>
        </Row>
        <ManageLevelsModal
          show={showDeleteLevelsModal !== null}
          onHide={() => {
            setShowDeleteLevelsModal(null);
            setLevelsToDelete({ ce: [], pe: [] });
          }}
          indexName={showDeleteLevelsModal}
          selectedLevels={selectedLevels}
          levelsToDelete={levelsToDelete}
          handleLevelCheckboxChange={handleLevelCheckboxChange}
          handleDeleteLevels={handleDeleteLevels}
        />
        <Row className="mt-3">
          <Col>
            <UITest
              onSendSelectedStrikes={handleSelectedLevels}
              indices={Object.keys(configs)}
              strikeData={strikeData}
              configs={configs}
              tradingEnabled={tradingEnabled}
            />
          </Col>
        </Row>
      </Container>
    </TradingContextProvider>
  );
};

export default Welcome;